import {
  Alert,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, Show, Space, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {
  GetMarketingCodeResponse,
  GetWarehousesResponse,
  usePatchMarketingCodeMutation,
  usePostMarketingCodeMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {getOptionsFromWarehouses, handleApiError, useDuplicateErrorHandler} from '@dms/shared';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {MarketingCodeForm, MarketingCodeFormSchema} from '../types/MarketingCodeForm';

export interface MarketingCodeDetailFormProps extends RequiredTestIdProps {
  marketingCode: GetMarketingCodeResponse | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  isEditDisabled: boolean;
}

export function MarketingCodeDetailForm(props: MarketingCodeDetailFormProps) {
  const navigate = useNavigate();

  const {duplicateError, duplicateErrorHandler} = useDuplicateErrorHandler();

  const [postMarketingCode] = usePostMarketingCodeMutation();
  const [patchMarketingCode] = usePatchMarketingCodeMutation();

  const handleNavigateBack = () => navigate(settingsRoutes.warehousesMarketingCode);

  const handleSubmit: FormSubmitHandler<MarketingCodeForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isNotNil(props.marketingCode)) {
      return await patchMarketingCode({
        body: formValues,
        marketingCodeId: props.marketingCode.id,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await postMarketingCode(formValues)
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch(duplicateErrorHandler);
  };

  const defaultValues: Partial<MarketingCodeForm> = {
    warehouseId: props.marketingCode?.warehouseId,
    name: props.marketingCode?.name,
    externalCode: props.marketingCode?.externalCode,
    salesBasePriceMarkup: props.marketingCode?.salesBasePriceMarkup,
    warrantyPriceMarkup: props.marketingCode?.warrantyPriceMarkup,
  };

  return (
    <Form<MarketingCodeForm>
      experimentalZodSchema={MarketingCodeFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isDisabled={props.isEditDisabled}
      shouldWatchForUnsavedChanges
    >
      {(control) => (
        <VStack spacing={4} width="50%" data-testid={props['data-testid']}>
          <Show when={duplicateError}>
            <Alert
              variant="error"
              title={i18n.t('entity.warehouse.labels.marketingCodeAlreadyExists')}
              data-testid={suffixTestId('duplicityAlert', props)}
            />
          </Show>

          <Grid columns={2}>
            <FormField
              isNotClearable
              isRequired
              isDisabled={isNotNil(props.marketingCode)}
              control={control}
              type="choice"
              name="warehouseId"
              label={i18n.t('entity.warehouse.labels.warehouse')}
              options={getOptionsFromWarehouses(props.warehouses)}
              data-testid={suffixTestId('inputs.warehouseId', props)}
            />
          </Grid>

          <Separator />

          <Grid columns={2}>
            <FormField
              isRequired
              control={control}
              type="text"
              name="name"
              label={i18n.t('general.labels.name')}
              data-testid={suffixTestId('inputs.name', props)}
            />
            <FormField
              isRequired
              control={control}
              type="text"
              name="externalCode"
              label={i18n.t('general.labels.externalCode')}
              data-testid={suffixTestId('inputs.externalCode', props)}
            />
          </Grid>

          <Grid columns={2}>
            <FormField
              control={control}
              type="number"
              isStepperVisible
              name="salesBasePriceMarkup"
              label={`${i18n.t('entity.warehouse.labels.salesBasePriceMarkup')} (%)`}
              data-testid={suffixTestId('inputs.salesBasePriceMarkup', props)}
            />
            <FormField
              control={control}
              type="number"
              isStepperVisible
              name="warrantyPriceMarkup"
              label={`${i18n.t('entity.warehouse.labels.warrantyPriceMarkup')} (%)`}
              data-testid={suffixTestId('inputs.warrantyPriceMarkup', props)}
            />
          </Grid>

          <Space fillAvailable />

          <SettingsFooter
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: isNil(props.marketingCode)
                  ? i18n.t('general.actions.discard')
                  : i18n.t('general.actions.discardChanges'),
                onClick: handleNavigateBack,
                'data-testid': suffixTestId('actions.discard', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: isNil(props.marketingCode)
                  ? i18n.t('general.actions.save')
                  : i18n.t('general.actions.saveChanges'),
                'data-testid': suffixTestId('actions.submit', props),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
